/**
 * Relatório financeiro SaaS (mensalidades) — página imprimível / Guardar como PDF.
 * Logo: /logo-ominia-hub-ring-1080-transparent.png
 */

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function moneyBr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

function billingLabel(row, refTodayYmd) {
  const td = String(refTodayYmd || '').slice(0, 10);
  const due = String(row?.due_date || '').slice(0, 10);
  if (String(row?.status) === 'paid') return 'Paga';
  if (String(row?.status) === 'cancelled') return 'Cancelada';
  if (String(row?.status) !== 'pending') return String(row?.status || '—');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return 'Em aberto';
  if (due < td) return 'Em atraso';
  if (due === td) return 'Vence hoje';
  return 'Em aberto';
}

/**
 * @param {{
 *   charges: Array<Record<string, unknown>>,
 *   filters?: Record<string, string>,
 *   todayYmd?: string,
 * }} payload
 */
export function openSaasFinanceReportPrintWindow(payload) {
  const charges = Array.isArray(payload?.charges) ? payload.charges : [];
  const filters = payload?.filters && typeof payload.filters === 'object' ? payload.filters : {};
  const todayYmd = String(payload?.todayYmd || new Date().toISOString().slice(0, 10)).slice(0, 10);
  const generated = new Date().toLocaleString('pt-BR');
  const logoUrl = `${window.location.origin}/logo-ominia-hub-ring-1080-transparent.png`;

  let totalAll = 0;
  let totalPaid = 0;
  let totalPending = 0;
  let totalOverdue = 0;
  let countPaid = 0;
  let countPending = 0;
  let countOverdue = 0;
  let countCancelled = 0;

  for (const row of charges) {
    const amt = Number(row.amount_brl) || 0;
    totalAll += amt;
    const st = String(row.status || '');
    const due = String(row.due_date || '').slice(0, 10);
    if (st === 'paid') {
      totalPaid += amt;
      countPaid += 1;
    } else if (st === 'cancelled') {
      countCancelled += 1;
    } else if (st === 'pending') {
      totalPending += amt;
      countPending += 1;
      if (/^\d{4}-\d{2}-\d{2}$/.test(due) && due < todayYmd) {
        totalOverdue += amt;
        countOverdue += 1;
      }
    }
  }

  const filterBits = [];
  if (filters.periodo) filterBits.push(`Período: ${filters.periodo}`);
  if (filters.status) filterBits.push(`Status: ${filters.status}`);
  if (filters.empresa) filterBits.push(`Empresa: ${filters.empresa}`);
  if (filters.dono) filterBits.push(`Dono: ${filters.dono}`);
  if (filters.sh) filterBits.push(`SH: ${filters.sh}`);
  const filterLine = filterBits.length ? filterBits.join(' · ') : 'Sem filtros — todas as cobranças carregadas';

  const rowsHtml = charges
    .map((row) => {
      const st = billingLabel(row, todayYmd);
      const due = String(row.due_date || '').slice(0, 10);
      const overdue = String(row.status) === 'pending' && /^\d{4}-\d{2}-\d{2}$/.test(due) && due < todayYmd;
      const shName = row.software_house_nome ? String(row.software_house_nome) : '—';
      return `<tr class="${overdue ? 'row-late' : ''}">
        <td class="mono">${escapeHtml(row.id)}</td>
        <td>
          <div class="store">${escapeHtml(row.tenant_nome)}</div>
          <div class="sub">@${escapeHtml(row.slug)} · ${escapeHtml(row.owner_name)} (#${escapeHtml(row.owner_user_id)})</div>
        </td>
        <td>${escapeHtml(shName)}</td>
        <td class="num">${escapeHtml(moneyBr(row.amount_brl))}</td>
        <td><span class="pill pill-${escapeHtml(String(row.status))}">${escapeHtml(st)}</span></td>
        <td class="mono">${escapeHtml(due || '—')}</td>
        <td class="mono">${escapeHtml(row.period_label || '—')}</td>
        <td class="mono">${escapeHtml(row.paid_at ? String(row.paid_at).slice(0, 19) : '—')}</td>
      </tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8"/>
<title>Relatório financeiro SaaS — OminIA</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@600;700;800&display=swap');
  :root {
    --ink: #0b1220;
    --muted: #5b6577;
    --line: #d8dee8;
    --paper: #f7f9fc;
    --cyan: #06b6d4;
    --navy: #0f172a;
    --late: #fff1f2;
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    color: var(--ink);
    background: #e8eef5;
    font-family: 'DM Sans', Segoe UI, sans-serif;
    font-size: 11.5px;
  }
  .sheet {
    max-width: 1100px;
    margin: 18px auto;
    background: #fff;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.12);
  }
  .hero {
    position: relative;
    background:
      radial-gradient(ellipse 80% 120% at 100% 0%, rgba(6, 182, 212, 0.22), transparent 55%),
      radial-gradient(ellipse 60% 80% at 0% 100%, rgba(99, 102, 241, 0.18), transparent 50%),
      linear-gradient(145deg, #070d18 0%, #101a2e 55%, #0c1524 100%);
    color: #e8f4ff;
    padding: 28px 32px 26px;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 22px;
    align-items: center;
  }
  .hero::after {
    content: '';
    position: absolute;
    inset: auto 0 0 0;
    height: 3px;
    background: linear-gradient(90deg, #22d3ee, #6366f1, #22d3ee);
  }
  .logo {
    width: 72px;
    height: 72px;
    object-fit: contain;
    filter: drop-shadow(0 0 12px rgba(34, 211, 238, 0.35));
  }
  .hero h1 {
    font-family: Syne, sans-serif;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 4px;
  }
  .hero .tag {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #7dd3fc;
    margin: 0 0 8px;
  }
  .hero .meta { color: #94a3b8; font-size: 11px; line-height: 1.55; }
  .body { padding: 22px 28px 28px; }
  .kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }
  .kpi {
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 14px 14px 12px;
    background: linear-gradient(180deg, #fff, var(--paper));
  }
  .kpi strong {
    display: block;
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 6px;
  }
  .kpi .v {
    font-family: Syne, sans-serif;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .kpi .d { margin-top: 4px; font-size: 10px; color: var(--muted); }
  .filters {
    margin-bottom: 16px;
    padding: 10px 14px;
    border-radius: 12px;
    background: var(--paper);
    border: 1px dashed #c5cedd;
    color: var(--muted);
    font-size: 11px;
  }
  h2 {
    font-family: Syne, sans-serif;
    font-size: 13px;
    margin: 0 0 10px;
    letter-spacing: -0.01em;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 8px 9px; border-bottom: 1px solid var(--line); text-align: left; vertical-align: top; }
  th {
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #64748b;
    background: #f1f5f9;
  }
  td.num { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
  .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 10px; }
  .store { font-weight: 600; }
  .sub { color: var(--muted); font-size: 10px; margin-top: 2px; }
  .row-late { background: var(--late); }
  .pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
  }
  .pill-paid { background: #d1fae5; color: #065f46; }
  .pill-pending { background: #e0f2fe; color: #075985; }
  .pill-cancelled { background: #e2e8f0; color: #475569; }
  .foot {
    margin-top: 18px;
    padding-top: 12px;
    border-top: 1px solid var(--line);
    color: var(--muted);
    font-size: 10px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .no-print { margin: 16px auto; max-width: 1100px; padding: 0 8px; }
  .btn {
    padding: 11px 18px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border-radius: 10px;
    border: 0;
    background: var(--navy);
    color: #fff;
  }
  .hint { margin: 8px 0 0; color: #64748b; font-size: 12px; }
  @media print {
    body { background: #fff; }
    .sheet { margin: 0; box-shadow: none; border-radius: 0; max-width: none; }
    .no-print { display: none; }
    .hero { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .kpi, .row-late, th, .pill { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  @media (max-width: 900px) {
    .kpis { grid-template-columns: 1fr 1fr; }
    .hero { grid-template-columns: 1fr; text-align: center; justify-items: center; }
  }
</style>
</head>
<body>
  <div class="no-print">
    <button type="button" class="btn" onclick="window.print()">Gerar PDF do relatório</button>
    <p class="hint">Clique no botão acima. Na janela que abrir, escolha <strong>Microsoft Print to PDF</strong> ou <strong>Guardar como PDF</strong> e confirme.</p>
  </div>
  <div class="sheet">
    <header class="hero">
      <img class="logo" src="${escapeHtml(logoUrl)}" alt="OminIA Hub" width="72" height="72"/>
      <div>
        <p class="tag">OminIA Pedidos SaaS</p>
        <h1>Relatório financeiro · mensalidades</h1>
        <div class="meta">
          Gerado em ${escapeHtml(generated)} · referência ${escapeHtml(todayYmd)}<br/>
          ${escapeHtml(charges.length)} cobrança(s) neste relatório
        </div>
      </div>
    </header>
    <div class="body">
      <div class="kpis">
        <div class="kpi"><strong>Total listado</strong><div class="v">${escapeHtml(moneyBr(totalAll))}</div><div class="d">${escapeHtml(String(charges.length))} linhas</div></div>
        <div class="kpi"><strong>Liquidado</strong><div class="v">${escapeHtml(moneyBr(totalPaid))}</div><div class="d">${escapeHtml(String(countPaid))} paga(s)</div></div>
        <div class="kpi"><strong>Em aberto</strong><div class="v">${escapeHtml(moneyBr(totalPending))}</div><div class="d">${escapeHtml(String(countPending))} · canceladas ${escapeHtml(String(countCancelled))}</div></div>
        <div class="kpi"><strong>Em atraso</strong><div class="v">${escapeHtml(moneyBr(totalOverdue))}</div><div class="d">${escapeHtml(String(countOverdue))} pendente(s) vencida(s)</div></div>
      </div>
      <div class="filters">${escapeHtml(filterLine)}</div>
      <h2>Detalhe das cobranças</h2>
      <table>
        <thead>
          <tr>
            <th>Cód.</th>
            <th>Loja / responsável</th>
            <th>SH</th>
            <th style="text-align:right">Valor</th>
            <th>Status</th>
            <th>Vencimento</th>
            <th>Período</th>
            <th>Pago em</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml || '<tr><td colspan="8">Nenhuma cobrança nos filtros actuais.</td></tr>'}
        </tbody>
      </table>
      <div class="foot">
        <span>Documento interno · super_admin</span>
        <span>OminIA Hub · pedido.ominia.app</span>
      </div>
    </div>
  </div>
</body>
</html>`;

  const w = window.open('', '_blank');
  if (!w) {
    window.alert('Permita pop-ups para gerar o PDF.');
    return;
  }
  try {
    w.opener = null;
  } catch {
    /* ignore */
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
